import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import type maplibregl from 'maplibre-gl'
import { AlertTriangle, Crosshair } from 'lucide-react'
import { MapContainer, DraggableMarker, SITE_ZOOM } from 'features/Map'
import { AddressAutocomplete } from 'components/AddressAutocomplete'
import type { AddressResult } from 'components/AddressAutocomplete'
import { Button } from 'components/ui/button'
import { reverseGeocodeCoords } from 'slices/evaluationSiteSlice'
import {
	fieldsFromAddressResult,
	fieldsFromGeocodeResult,
	type CapturedSiteFields,
} from 'utils/siteCaptureFields'

export type { CapturedSiteFields }

export interface MapPointPickerProps {
	/** Seeds the pin when editing a record that already has coordinates. */
	initialLatitude?: number
	initialLongitude?: number
	onCapture: (captured: CapturedSiteFields) => void
	/** Label on the capture button, e.g. "Capture Site" / "Capture Location". */
	captureLabel?: string
	capturingLabel?: string
}

/**
 * Search for an area, drop a pin, drag it to the exact spot, then capture the
 * address into the form around it.
 *
 * Exists because Places autocomplete cannot describe a raw parcel, a
 * greenfield corner, or a pad site that has no address yet. Those are exactly
 * the places an evaluation is built to compare, and increasingly the places a
 * location record has to stand for too.
 *
 * Generalised out of the evaluations-only SiteMapPicker so Add Location and
 * New Site place a pin the same way. The geocoder-to-fields mapping lives in
 * `utils/siteCaptureFields` rather than here: the copy that used to live in
 * this component had already drifted from it, so the same pin produced
 * different addresses depending on which screen captured it.
 */
export function MapPointPicker({
	initialLatitude,
	initialLongitude,
	onCapture,
	captureLabel = 'Capture Site',
	capturingLabel = 'Capturing...',
}: MapPointPickerProps) {
	const dispatch = useDispatch()

	const [map, setMap] = useState<maplibregl.Map | null>(null)
	const [pin, setPin] = useState<{ lng: number; lat: number } | null>(
		Number.isFinite(initialLatitude as number) && Number.isFinite(initialLongitude as number)
			? { lng: initialLongitude as number, lat: initialLatitude as number }
			: null,
	)
	const [capturing, setCapturing] = useState(false)
	const [warning, setWarning] = useState<string | null>(null)
	/**
	 * The centre of the visible map, fed to the search box as its ranking
	 * origin.
	 *
	 * Someone looking at a map and typing into the box on top of it means
	 * "near here", not "near my laptop". This also rescues the case the
	 * browser cannot: a coarse IP fix puts the user in the wrong town, but
	 * once they have panned to the right one the search follows them.
	 */
	const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number } | null>(null)

	// The last Places pick, and whether the pin has moved since. An untouched
	// pin can reuse the structured components we already have instead of
	// paying for a reverse lookup that would return a coarser address.
	const lastResultRef = useRef<AddressResult | null>(null)
	const pinMovedRef = useRef(false)

	// One-shot. Without the guard, a re-render would re-fly the camera and
	// yank it off a pin the user had just dragged.
	const didCenterRef = useRef(false)

	useEffect(() => {
		if (!map || didCenterRef.current || !pin) return
		didCenterRef.current = true
		// `useMapLibre` snapshots its center/zoom props at mount, so the
		// camera has to be driven imperatively rather than by prop.
		map.flyTo({ center: [pin.lng, pin.lat], zoom: SITE_ZOOM, duration: 0 })
	}, [map, pin])

	// Track the camera so the search box can rank around it.
	useEffect(() => {
		if (!map) return
		const readCenter = () => {
			try {
				const c = map.getCenter()
				setMapCenter({ lat: c.lat, lng: c.lng })
			} catch {
				// Map already destroyed.
			}
		}
		readCenter()
		map.on('moveend', readCenter)
		return () => {
			try {
				map.off('moveend', readCenter)
			} catch {
				// Map already destroyed.
			}
		}
	}, [map])

	// Click to drop, so a search that returns nothing usable is still workable.
	useEffect(() => {
		if (!map) return
		const handleClick = (e: maplibregl.MapMouseEvent) => {
			pinMovedRef.current = true
			setWarning(null)
			setPin({ lng: e.lngLat.lng, lat: e.lngLat.lat })
		}
		map.on('click', handleClick)
		try {
			const canvas = map.getCanvas()
			if (canvas) canvas.style.cursor = 'crosshair'
		} catch {
			// The map is gone; nothing to style.
		}

		return () => {
			// React unmounts children before parents, so MapContainer has
			// already called map.remove() by the time this runs on teardown.
			// Both calls tolerate that, but guard rather than rely on it.
			try {
				map.off('click', handleClick)
				const canvas = map.getCanvas()
				if (canvas) canvas.style.cursor = ''
			} catch {
				// Map already destroyed.
			}
		}
	}, [map])

	const handleAddressSelect = useCallback(
		(result: AddressResult) => {
			lastResultRef.current = result
			pinMovedRef.current = false
			setWarning(null)
			setPin({ lng: result.longitude, lat: result.latitude })
			map?.flyTo({
				center: [result.longitude, result.latitude],
				zoom: SITE_ZOOM,
				duration: 900,
				essential: true,
			})
		},
		[map],
	)

	const handleDragEnd = useCallback(({ lng, lat }: { lng: number; lat: number }) => {
		pinMovedRef.current = true
		setWarning(null)
		setPin({ lng, lat })
	}, [])

	const handleCapture = useCallback(async () => {
		if (!pin) return
		setCapturing(true)
		setWarning(null)
		try {
			// Pin untouched since a Places pick: reuse what we already have.
			if (!pinMovedRef.current && lastResultRef.current) {
				onCapture(fieldsFromAddressResult(lastResultRef.current, pin))
				return
			}

			const action: any = await (dispatch as any)(
				reverseGeocodeCoords({ lat: pin.lat, lng: pin.lng }),
			)
			const result = action?.meta?.requestStatus === 'fulfilled' ? action.payload : null

			onCapture(fieldsFromGeocodeResult(result, pin))
			if (!result) {
				setWarning(
					'No address found at this point. The coordinates were captured, so fill in the address fields by hand.',
				)
			}
		} finally {
			setCapturing(false)
		}
	}, [dispatch, onCapture, pin])

	return (
		<div className="space-y-3">
			<p className="text-xs italic text-[#0080fe]">
				Search for a city, address or postal code to zoom the map, then drag the pin (or
				click the map) to place it exactly. {captureLabel} fills in the form for you.
			</p>

			{/* Explicit height: a zero-height first paint leaves MapLibre with
			    nothing to render into, and the ResizeObserver only rescues it
			    once the container actually has a size. */}
			<div className="relative h-[480px] w-full overflow-hidden rounded-md border border-gray-200">
				<MapContainer onMapLoad={setMap}>
					{pin && (
						<DraggableMarker
							map={map}
							longitude={pin.lng}
							latitude={pin.lat}
							onDragEnd={handleDragEnd}
						/>
					)}
				</MapContainer>

				{/* Overlay siblings, same convention as MapLegend/MapControls.
				    AddressAutocomplete's dropdown is already absolute/z-50
				    inside its own wrapper, so it layers over the canvas. */}
				<div className="absolute left-3 top-3 z-20 w-[min(420px,calc(100%-1.5rem))]">
					<div className="rounded-md border border-gray-200 bg-white/95 p-2 shadow-md backdrop-blur">
						<AddressAutocomplete
							placeholder="Search a city, address or postal code..."
							onSelect={handleAddressSelect}
							proximity={mapCenter || undefined}
						/>
					</div>
				</div>

				<div className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-3 rounded-md border border-gray-200 bg-white/95 px-3 py-2 shadow-md backdrop-blur">
					<span className="flex items-center gap-1.5 whitespace-nowrap text-xs text-gray-600">
						<Crosshair className="h-3.5 w-3.5 text-gray-400" />
						{pin
							? `${pin.lat.toFixed(5)}, ${pin.lng.toFixed(5)}`
							: 'Search or click the map to drop a pin'}
					</span>
					<Button
						type="button"
						variant="primary"
						disabled={!pin || capturing}
						onClick={handleCapture}
					>
						{capturing ? capturingLabel : captureLabel}
					</Button>
				</div>
			</div>

			{warning && (
				<div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
					<AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
					<span>{warning}</span>
				</div>
			)}
		</div>
	)
}

export default MapPointPicker
